import { useState } from 'react';
import { Phone, Send, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ServiceContactCTAProps {
  serviceTitle: string;
}

const ServiceContactCTA = ({ serviceTitle }: ServiceContactCTAProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  return (
    <section className="px-6 lg:px-8 py-16 md:py-20 bg-petrol text-petrol-foreground">
      <div className="max-w-5xl mx-auto">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
          {/* Left — CTA text */}
          <div className="flex flex-col justify-center">
            <p className="text-xs font-heading font-semibold uppercase tracking-[0.15em] text-primary mb-2">Parlons de votre projet</p>
            <h2 className="font-heading text-2xl md:text-3xl font-bold mb-4">
              Envie d'en savoir plus ?
            </h2>
            <p className="text-sm text-petrol-foreground/60 leading-relaxed mb-6">
              Chaque projet est unique. Contactez-nous pour un premier échange gratuit et sans engagement. Nous vous répondons sous 48 à 72h.
            </p>

            <div className="space-y-4">
              <a
                href="tel:+33671351550"
                className="flex items-center gap-3 p-4 rounded-xl border border-petrol-foreground/10 bg-petrol-foreground/5 hover:border-primary/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Phone size={16} className="text-primary" />
                </div>
                <div>
                  <p className="font-heading text-sm font-bold text-petrol-foreground">Appelez-nous directement</p>
                  <p className="text-xs text-petrol-foreground/50">06 71 35 15 50 — Lun-Ven 9h-19h · Sam 10h-18h</p>
                </div>
              </a>

              <Link
                to="/contact"
                onClick={() => window.scrollTo(0, 0)}
                className="flex items-center gap-3 p-4 rounded-xl border border-petrol-foreground/10 bg-petrol-foreground/5 hover:border-primary/30 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                  <Send size={16} className="text-primary" />
                </div>
                <div>
                  <p className="font-heading text-sm font-bold text-petrol-foreground">Demander un devis détaillé</p>
                  <p className="text-xs text-petrol-foreground/50">Formulaire complet avec choix de prestation</p>
                </div>
              </Link>
            </div>
          </div>

          {/* Right — Quick contact form */}
          <div className="rounded-2xl border border-petrol-foreground/10 bg-petrol-foreground/5 p-6 md:p-8">
            <h3 className="font-heading text-lg font-bold text-petrol-foreground mb-1">Message rapide</h3>
            <p className="text-xs text-petrol-foreground/50 mb-5">Décrivez votre projet, nous vous recontactons sous 48 à 72h.</p>

            <form
              onSubmit={(e) => {
                e.preventDefault();
                window.location.href = `/contact?service=${encodeURIComponent(serviceTitle)}&name=${encodeURIComponent(name)}&email=${encodeURIComponent(email)}&message=${encodeURIComponent(message)}`;
              }}
              className="space-y-4"
            >
              <div>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Votre nom"
                  required
                  className="w-full rounded-lg border border-petrol-foreground/10 bg-petrol-foreground/5 px-4 py-3 text-sm text-petrol-foreground placeholder:text-petrol-foreground/30 outline-none focus:border-primary/40 transition-colors"
                />
              </div>
              <div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Votre email"
                  required
                  className="w-full rounded-lg border border-petrol-foreground/10 bg-petrol-foreground/5 px-4 py-3 text-sm text-petrol-foreground placeholder:text-petrol-foreground/30 outline-none focus:border-primary/40 transition-colors"
                />
              </div>
              <div>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={`Je souhaite en savoir plus sur ${serviceTitle}…`}
                  rows={3}
                  required
                  className="w-full rounded-lg border border-petrol-foreground/10 bg-petrol-foreground/5 px-4 py-3 text-sm text-petrol-foreground placeholder:text-petrol-foreground/30 outline-none focus:border-primary/40 transition-colors resize-none"
                />
              </div>
              <button
                type="submit"
                className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-full px-6 py-3 font-heading font-medium text-sm hover:bg-primary/90 transition-colors"
              >
                Envoyer ma demande <ArrowRight size={14} />
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceContactCTA;
